<script setup lang="ts">
import { ref } from 'vue'

import { routeNames } from '@/router/routeNames'

import PathwayLandingBrandMark from './PathwayLandingBrandMark.vue'

interface NavLink {
  label: string
  href: string
  active?: boolean
}

const navLinks: NavLink[] = [
  { label: 'Konfigurator', href: '#', active: true },
  { label: 'Pojazdy', href: '#' },
  { label: 'Kalkulator', href: '#' },
  { label: 'Realizacje', href: '#' },
  { label: 'O nas', href: '#' },
  { label: 'Kontakt', href: '#contact' },
]

const menuOpen = ref<boolean>(false)

function closeMenuAndScroll(href: string): void {
  if (href === '#') {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  } else if (href.startsWith('#')) {
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' })
  }
  setTimeout(() => {
    menuOpen.value = false
  }, 50)
}
</script>

<template>
  <nav
    class="border-pw-line bg-pw-ink/90 sticky top-0 z-50 border-b backdrop-blur-md"
  >
    <div
      class="mx-auto flex h-16 max-w-360 items-center justify-between px-5 md:px-14"
    >
      <a
        href="#"
        class="text-pw-paper inline-flex shrink-0 items-center gap-2.5 text-[18px] font-extrabold tracking-tight no-underline"
      >
        <PathwayLandingBrandMark />
        <span
          >FLEX
          <span
            class="text-pw-muted ml-1.5 hidden font-mono text-[13px] font-medium tracking-[0.05em] sm:inline"
            >/ KONFIGURATOR</span
          >
        </span>
      </a>

      <ul class="m-0 hidden list-none gap-1 p-0 lg:flex">
        <li v-for="link in navLinks" :key="link.label">
          <a
            :href="link.href"
            class="hover:bg-pw-ink-3 rounded-[2px] px-3.5 py-2 text-[14px] font-medium tracking-tight no-underline transition-colors"
            :class="link.active ? 'text-pw-orange' : 'text-pw-paper'"
            >{{ link.label }}</a
          >
        </li>
      </ul>

      <div class="mr-3 ml-auto hidden items-center gap-3 md:flex">
        <RouterLink
          :to="{ name: routeNames.Login }"
          class="bg-pw-line text-pw-ink hover:border-pw-paper inline-flex cursor-pointer items-center gap-2 rounded-[2px] border border-[#2a2e36] px-4 py-2 text-[13px] font-semibold no-underline transition-all hover:bg-[#a59e8b]"
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <circle cx="12" cy="8" r="4" />
            <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
          </svg>
          Zaloguj
        </RouterLink>
        <a
          href="#config"
          class="bg-pw-orange text-pw-paper hover:bg-pw-yellow inline-flex cursor-pointer items-center gap-2 rounded-[2px] px-4 py-2 text-[13px] font-semibold no-underline transition-all"
        >
          Skonfiguruj
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
          >
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </a>
      </div>

      <button
        class="border-pw-line text-pw-paper hover:border-pw-paper grid h-9 w-9 cursor-pointer place-items-center border transition-colors lg:hidden"
        :aria-expanded="menuOpen"
        aria-label="Menu"
        @click="menuOpen = !menuOpen"
      >
        <svg
          v-if="!menuOpen"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M3 6h18M3 12h18M3 18h18" />
        </svg>
        <svg
          v-else
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>
    </div>

    <Transition name="menu">
      <div
        v-if="menuOpen"
        class="border-pw-line bg-pw-ink border-t px-5 pb-5 lg:hidden"
      >
        <ul class="m-0 mb-4 flex list-none flex-col p-0 pt-3">
          <li v-for="link in navLinks" :key="link.label">
            <a
              :href="link.href"
              class="block py-2.5 text-[15px] font-medium no-underline transition-colors"
              :class="link.active ? 'text-pw-orange' : 'text-pw-paper'"
              @click.prevent="closeMenuAndScroll(link.href)"
            >
              {{ link.label }}
            </a>
          </li>
        </ul>
        <div class="border-pw-line flex gap-3 border-t pt-4">
          <RouterLink
            :to="{ name: routeNames.Login }"
            class="bg-pw-line text-pw-ink inline-flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-[2px] border border-[#2a2e36] py-2.5 text-[14px] font-semibold no-underline"
            @click="menuOpen = false"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="12" cy="8" r="4" />
              <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
            </svg>
            Zaloguj
          </RouterLink>
          <a
            href="#config"
            class="bg-pw-orange text-pw-paper inline-flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-[2px] py-2.5 text-[14px] font-semibold no-underline"
            @click="menuOpen = false"
          >
            Skonfiguruj
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </a>
        </div>
      </div>
    </Transition>
  </nav>
</template>

<style scoped>
.menu-enter-active,
.menu-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}
.menu-enter-from,
.menu-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
